import { defineNuxtPlugin } from 'nuxt/app';

import { PatientAuthStore } from '@/auth/PatientAuthStore.js';
import { PatientChatLoader } from '@/chat/PatientChatLoader.js';
import { PatientFileMetadataLoader } from '@/files/PatientFileMetadataLoader.js';
import { PatientFileUploadUrlProvider } from '@/files/PatientFileUploadUrlProvider.js';
import { MarketingParamsStore } from '@/landing/MarketingParamsStore.js';
import { ApiClient } from '@/services/ApiClient.js';
import { StaffStore } from '@/services/StaffStore.js';
import { CommonPlugin } from '@nzyme/vue/CommonPlugin.js';
import { AuthStore } from '@sigma/client/auth/AuthStore.js';
import { FileMetadataStore } from '@sigma/client/files/FileMetadataStore.js';
import { setupApp } from '@sigma/nuxt-utils/setupApp.js';
import { PatientInitializeEndpoint } from '@sigma/patient-api/patient/PatientInitializeEndpoint.js';

export default defineNuxtPlugin({
    name: 'core-plugin',
    setup(nuxtApp) {
        const container = setupApp({
            name: 'patient',
            userType: 'PATIENT',
        });

        nuxtApp.vueApp.use(CommonPlugin);

        // Registered unconditionally: prerendered pages resolve services that depend on the auth
        // store, so the AuthStore interface must have an implementation on the server too. Only the
        // eager resolution below is client-only.
        container.register(PatientAuthStore);
        container.register(PatientChatLoader);
        container.register(PatientFileMetadataLoader);
        container.register(FileMetadataStore);
        container.register(PatientFileUploadUrlProvider);
        container.register(StaffStore);

        // Auth requires Cognito env variables that are not available during SSR/prerender,
        // and the marketing params capture reads window/sessionStorage — both client-only.
        if (!import.meta.client) {
            return;
        }

        // Setup auth
        const authStore = container.resolve(AuthStore);
        const apiClient = container.resolve(ApiClient);

        authStore.events.authenticated.on(async user => {
            if (!user) {
                await apiClient(PatientInitializeEndpoint, undefined, { authCheck: false });
                await authStore.refreshAuth();
            }
        });

        // Setup marketing params
        /**
         * Capture marketing params (utm_*, gclid, fbclid) from the entry URL into the {@link MarketingParamsStore}
         * (sessionStorage) on client load.
         *
         * Attribution is last-touch: an entry carrying marketing params overwrites the stored set; a param-less
         * entry leaves the stored set intact, so the campaign survives param-less internal navigations and
         * refreshes. The stored params are available to analytics (e.g. GTM) and used by `useLandingRoute` to keep
         * `utm_campaign` on landing links.
         */
        const marketingParams = container.resolve(MarketingParamsStore);
        marketingParams.capture(window.location.search);
    },
});
